import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type IncidentRow = {
  id: string;
  incident_no: string;
  incident_on: string;
  category: string;
  polarity?: string;
  severity: string;
  summary: string;
  status: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  section_name?: string;
};

export default function BehaviourListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.behaviour.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [polarity, setPolarity] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-behaviour', status, category, polarity, q],
    queryFn: async () => {
      const res = await api.get<IncidentRow[]>('/education/behaviour/incidents', {
        params: { status, category, polarity, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('behaviour')}
      description={t('behaviourIntro')}
      extra={
        canManage ? (
          <Button type="primary" onClick={() => navigate('/education/behaviour/new')}>
            {t('newBehaviourIncident')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'open', label: t('statusOpen') },
                  { value: 'in_progress', label: t('statusInProgress') },
                  { value: 'closed', label: t('statusClosed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('behaviourPolarity', { defaultValue: 'Type' })}>
              <Select
                allowClear
                style={{ minWidth: 150 }}
                value={polarity}
                onChange={(v) => setPolarity(v || undefined)}
                options={[
                  { value: 'concern', label: t('behaviourPolarityConcern', { defaultValue: 'Concern' }) },
                  { value: 'positive', label: t('behaviourPolarityPositive', { defaultValue: 'Positive recognition' }) },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('category')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={category}
                onChange={(v) => setCategory(v || undefined)}
                options={[
                  { value: 'recognition', label: t('behaviourCategoryRecognition', { defaultValue: 'Recognition' }) },
                  { value: 'leadership', label: t('behaviourCategoryLeadership', { defaultValue: 'Leadership' }) },
                  { value: 'service', label: t('behaviourCategoryService', { defaultValue: 'Service' }) },
                  { value: 'improvement', label: t('behaviourCategoryImprovement', { defaultValue: 'Improvement' }) },
                  { value: 'other_positive', label: t('behaviourCategoryOtherPositive', { defaultValue: 'Other positive' }) },
                  { value: 'disruption', label: t('behaviourCategoryDisruption') },
                  { value: 'disrespect', label: t('behaviourCategoryDisrespect') },
                  { value: 'property_damage', label: t('behaviourCategoryProperty') },
                  { value: 'peer_conflict', label: t('behaviourCategoryPeer') },
                  { value: 'academic_integrity', label: t('behaviourCategoryAcademic') },
                  { value: 'other', label: t('behaviourCategoryOther') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/behaviour/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('incidentNo'), dataIndex: 'incident_no', width: 150 },
              { title: t('incidentOn'), dataIndex: 'incident_on', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, row: IncidentRow) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              {
                title: t('behaviourPolarity', { defaultValue: 'Type' }),
                dataIndex: 'polarity',
                width: 100,
                render: (v?: string) => (
                  <Tag color={v === 'positive' ? 'green' : undefined}>
                    {v === 'positive'
                      ? t('behaviourPolarityPositive', { defaultValue: 'Positive recognition' })
                      : t('behaviourPolarityConcern', { defaultValue: 'Concern' })}
                  </Tag>
                ),
              },
              { title: t('category'), dataIndex: 'category', width: 130 },
              {
                title: t('severity'),
                dataIndex: 'severity',
                width: 100,
                render: (v: string) => <Tag color={v === 'high' ? 'red' : v === 'medium' ? 'orange' : undefined}>{v}</Tag>,
              },
              { title: t('summary'), dataIndex: 'summary', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
